import { View, Image, useWindowDimensions, Pressable, Animated, Platform } from "react-native";
import { useDispatch, useSelector } from 'react-redux';
import { selectUserAvatar } from '@reduxLocal/selectors/userSelectors';
import { Text } from "@constants/Themed";
import { HIDE_HELP_MODAL, TOGGLE_EASY_MODE } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import React, { useCallback, useEffect, useRef, useState, useContext, useMemo, lazy, Suspense } from 'react';
import { LinearGradient } from "expo-linear-gradient";
import { MaterialIcons } from '@expo/vector-icons';
import SSLogo from "@assets/--native/Logo";
import styles from "@stylesheet";
import { Title } from "@constants/Themed";
import Constants from 'expo-constants';
import FlipCard from "react-native-flip-card";
import * as Animatable from 'react-native-animatable';
import AnimatedLottieView from '@utils/lottieAnimation';
import { GiftedChat } from "react-native-gifted-chat";
import useGlobalUser from "@hooks/getGlobalUser";
import MyInputToolbar from "@navigation/screens/MidLevel/MessageScreen/Components/InputToolbar";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import moment from "moment";
import { debounce, throttle, uniqueId } from "lodash";
import { AI_CHAT_API_URL, AI_MODEL } from "@api/constant";
import { XStack } from "tamagui";
import ActionArray from "@components/UI/ContextActions.tsx/ActionArray";
import { ToolbarContext, useEditorContext } from "@hooks/toolbarContext";
import handleHelp, { convertGiftedChatToHistory, sanitizeHistoryForAPI } from "./utils/handleHelp";
import ChatInterface from './Components/ChatInterface';
import { useUtilities } from '../../../contexts/UtilitiesContext';
import {
  analyzeResponse,
  isMapperReady
} from '@utils/semanticActionMapper';
import { ActionChips, ActionDataForm, PostExecutionFeedback } from '@components/ActionConfirmation';
import type { ActionTemplate, MatchedAction } from '@utils/semanticActionMapper';
import { isSemanticActionMapperReady } from '@utils/semanticActionMapperSingleton';
import { useNavigation } from '@react-navigation/native';
import { extractDataHybrid } from '@utils/nlpDataExtractor';
import { getSchemaForAction } from '@utils/schemaCache';
import { submitFeedback, preloadFeedbackData } from '@utils/actionFeedbackManager';

// Lazy load heavy components to improve initial performance
const ShowHelp = lazy(() => import("./Components/SearchTopics"));
const HelpChat = lazy(() => import("./Components/HelpChat"));

// Safe Markdown Component for AI responses
interface SafeMarkDownProps {
  content: string;
}

const SafeMarkDown: React.FC<SafeMarkDownProps> = React.memo(({ content }) => {
  // Validate content before rendering
  if (!content || typeof content !== 'string' || content.trim() === '') {
    return (
      <Text style={{ fontSize: 13, color: '#666', lineHeight: 20 }}>
        No content available
      </Text>
    );
  }

  // Clean the content to prevent parsing errors
  const cleanContent = content
    .replace(/\r\n/g, '\n') // Normalize line endings
    .replace(/\t/g, '  ') // Replace tabs with spaces
    .replace(/<think>/g, '') // Remove thinking tags
    .replace(/<\/think>/g, '') // Remove thinking tags
    .trim();

  // Don't use the problematic MarkDown library - just render as plain text
  return (
    <Text style={{
      fontSize: 13,
      lineHeight: 20,
      color: '#333'
    }}>
      {cleanContent}
    </Text>
  );
});

const HelpSidePanel = React.memo(() => {
  const currentScreen = useSelector((state: DefaultRootState) => state.navigation.page.name);
  const theme = themeSelector();
  const [flip, setFlip] = useState(false);
  const [selection, setSelection] = useState();
  const [helpSearch, setHelpSearch] = useState("");
  const [AIHelp, setAIHelp] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatStarted, setChatStarted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [chatHistoryRefreshTrigger, setChatHistoryRefreshTrigger] = useState(0);

  // Semantic Action Mapper state
  const [pendingActions, setPendingActions] = useState<MatchedAction[]>([]);
  const [actionRequiringData, setActionRequiringData] = useState<ActionTemplate | null>(null);
  const [isMapperInitialized, setIsMapperInitialized] = useState(false);
  const [extractedFormData, setExtractedFormData] = useState<Record<string, any>>({});
  const [currentUserQuery, setCurrentUserQuery] = useState<string>('');
  const [showPostExecutionFeedback, setShowPostExecutionFeedback] = useState(false);
  const [lastExecutedAction, setLastExecutedAction] = useState<{ template: ActionTemplate; data?: any } | null>(null);
  const { width } = useWindowDimensions();
  const animatedWidth = useRef(new Animated.Value(400)).current;
  const globalUser = useGlobalUser();
  const [inputDisabled, disableInput] = useState(false);
  const userAvatar = useSelector(selectUserAvatar);
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  const visibleState = useSelector((state: DefaultRootState) => state.modals.helpModal.modalDisplayed);
  const navigation = useNavigation();

  // Check if semantic action mapper is ready (initialized at app startup)
  useEffect(() => {
    if (visibleState) {
      // Check if the singleton mapper is ready
      const checkMapperReady = () => {
        const isReady = isSemanticActionMapperReady();
        console.log('🔍 Checking mapper ready status:', { isReady, isMapperInitialized, visibleState });

        if (isReady && !isMapperInitialized) {
          setIsMapperInitialized(true);
          console.log('✅ Semantic action mapper is ready (initialized at app startup)');
        } else if (!isReady) {
          // If not ready yet, check again in a bit
          setTimeout(checkMapperReady, 500);
        }
      };

      checkMapperReady();
    }
  }, [visibleState]);

  // Preload feedback data when user is available
  useEffect(() => {
    if (globalUser?._id && isMapperInitialized) {
      // Preload feedback data for common action intents
      const commonActionIntents = [
        'SHOW_PROJECT_MODAL',
        'ADD_CALENDAR_ENTRY',
        'CREATE_EVENT_WITH_PROJECT',
        'MANAGE_DATA_SEQUENCE'
      ];

      preloadFeedbackData(commonActionIntents, globalUser._id);
    }
  }, [globalUser?._id, isMapperInitialized]);

  // Listen for detected actions from ChatInterface
  useEffect(() => {
    const handleActionsDetected = (event: CustomEvent) => {
      const { actions } = event.detail;
      if (actions && actions.length > 0) {
        setPendingActions(prev => {
          // Avoid duplicates
          const existingIntents = new Set(prev.map(a => a.intent));
          const newActions = actions.filter(a => !existingIntents.has(a.intent));
          return [...prev, ...newActions];
        });
      }
    };

    window.addEventListener('aiActionsDetected', handleActionsDetected as EventListener);

    return () => {
      window.removeEventListener('aiActionsDetected', handleActionsDetected as EventListener);
    };
  }, []);


  // Handle action execution
  const handleExecuteAction = useCallback(async (template: ActionTemplate, data?: any) => {
    console.log('🎯 Execute action called:', {
      intent: template.intent,
      requiresData: template.requiresData,
      hasSchema: !!template.schema,
      hasData: !!data,
      currentQuery: currentUserQuery
    });

    // If action requires data and no data provided, try to extract it
    if (template.requiresData && !data) {
      // Get schema from cache or template
      const schema = getSchemaForAction(template.intent) || template.schema;

      console.log('📋 Schema for action:', {
        intent: template.intent,
        hasCachedSchema: !!getSchemaForAction(template.intent),
        hasTemplateSchema: !!template.schema,
        usingSchema: !!schema
      });

      // Try to extract data from the user's query
      if (schema && currentUserQuery) {
        console.log('🔍 Attempting to extract data from user query...');
        const extraction = await extractDataHybrid(
          currentUserQuery,
          schema,
          template.description || template.intent
        );

        console.log('📊 Extraction result:', extraction);

        if (extraction.confidence > 0.6 && extraction.missingFields.length === 0) {
          // High confidence and all required fields found - execute immediately
          console.log('✅ Auto-filling form with extracted data');
          // Continue with execution using extracted data
          data = extraction.extractedData;
        } else if (extraction.confidence > 0.3) {
          // Medium confidence - show form with pre-filled data
          console.log('📝 Pre-filling form with partial data');
          setExtractedFormData(extraction.extractedData);
          setActionRequiringData(template);
          return;
        } else {
          // Low confidence - show empty form
          console.log('❌ Low confidence extraction, showing empty form');
          setExtractedFormData({});
          setActionRequiringData(template);
          return;
        }
      } else {
        // No schema or query - show empty form
        setActionRequiringData(template);
        return;
      }
    }

    // Execute the Redux action(s)
    try {
      // Capture data in a const to ensure it's available in setTimeout closures
      const executionData = data;

      // Execute action sequence if available, otherwise single action
      if (template.actionSequence && template.actionSequence.length > 0) {
        console.log('🔄 Executing action sequence with data:', executionData);

        // Execute each action in the sequence with a small delay
        template.actionSequence.forEach((actionFn, index) => {
          setTimeout(() => {
            const action = actionFn(executionData);

            console.log(`🎯 Action ${index + 1}/${template.actionSequence!.length}:`, action.type, 'payload:', action.payload);

            // Check if this is a navigation action
            if (action.type === 'REACT_NAVIGATE') {
              const { screen, stack, params } = action.payload;
              if (stack) {
                (navigation as any).navigate(stack, { screen, params });
              } else {
                (navigation as any).navigate(screen, params);
              }
            } else {
              dispatch(action);
            }
          }, index * 100); // 100ms delay between actions
        });
      } else {
        // Execute single action
        const action = template.reduxAction(executionData);

        // Check if this is a navigation action
        if (action.type === 'REACT_NAVIGATE') {
          const { screen, stack, params } = action.payload;
          if (stack) {
            (navigation as any).navigate(stack, { screen, params });
          } else {
            (navigation as any).navigate(screen, params);
          }
        } else {
          dispatch(action);
        }
      }

      // Remove from pending actions
      setPendingActions(prev => prev.filter(a => a.intent !== template.intent));

      // Close data form if open
      if (actionRequiringData) {
        setActionRequiringData(null);
      }

      // Store executed action for post-execution feedback
      setLastExecutedAction({ template, data });

      // Show post-execution feedback modal after a short delay
      setTimeout(() => {
        setShowPostExecutionFeedback(true);
      }, 1000);

    } catch (error) {
      console.error('❌ Failed to execute action:', error);
    }
  }, [dispatch, navigation, actionRequiringData, currentUserQuery]);

  // Handle action dismissal
  const handleDismissAction = useCallback((intent: string) => {
    setPendingActions(prev => prev.filter(a => a.intent !== intent));
  }, []);

  // Handle dismiss all actions
  const handleDismissAllActions = useCallback(() => {
    setPendingActions([]);
  }, []);

  // Handle data form submission
  const handleDataFormSubmit = useCallback((template: ActionTemplate, data: any) => {
    handleExecuteAction(template, data);
  }, [handleExecuteAction]);

  // Handle data form cancel
  const handleDataFormCancel = useCallback(() => {
    setActionRequiringData(null);
  }, []);

  // Handle inline feedback from action chips
  const handleInlineFeedback = useCallback(async (actionIntent: string, feedbackType: 'upvote' | 'downvote') => {
    try {
      const success = await submitFeedback({
        actionIntent,
        query: currentUserQuery,
        feedbackType,
        wasExecuted: false, // This is pre-execution feedback
        context: {
          screen: 'HelpSidePanel',
          companyId: globalUser?.currentCompany?._id,
          additionalData: {
            source: 'inline_chip'
          }
        }
      });

      if (success) {
        console.log('✅ Inline feedback submitted successfully');
      }
    } catch (error) {
      console.error('❌ Failed to submit inline feedback:', error);
    }
  }, [currentUserQuery, globalUser]);

  // Handle post-execution feedback
  const handlePostExecutionFeedback = useCallback(async (feedbackType: 'upvote' | 'downvote', comment?: string) => {
    if (!lastExecutedAction) return;

    try {
      const success = await submitFeedback({
        actionIntent: lastExecutedAction.template.intent,
        query: currentUserQuery,
        feedbackType,
        wasExecuted: true,
        executionSuccess: feedbackType === 'upvote',
        context: {
          screen: 'HelpSidePanel',
          companyId: globalUser?.currentCompany?._id,
          additionalData: {
            source: 'post_execution',
            comment: comment || null
          }
        }
      });

      if (success) {
        console.log('✅ Post-execution feedback submitted successfully');
      }
    } catch (error) {
      console.error('❌ Failed to submit post-execution feedback:', error);
    }
  }, [lastExecutedAction, currentUserQuery, globalUser]);

  // Callback to refresh chat history after saving
  const handleChatSaved = useCallback(() => {
    console.log('🔄 Chat saved - triggering history refresh');

    // Method 1: Dispatch a Redux action to trigger chat history refresh
    const refreshAction = { type: 'REFRESH_CHAT_HISTORY', payload: Date.now() };
    console.log('🔄 Dispatching refresh action:', refreshAction);
    dispatch(refreshAction);

    // Method 2: Also dispatch a custom event as backup
    const refreshEvent = new CustomEvent('chatHistoryRefresh', {
      detail: { timestamp: Date.now() }
    });
    window.dispatchEvent(refreshEvent);
    console.log('🔄 Dispatched custom event:', refreshEvent);
  }, [dispatch]);

  // Listen for chat restoration requests (Redux for native, window events for web)
  const chatRestoration = useSelector((state: DefaultRootState) => state.modals.helpModal.chatRestoration);
  const shouldExpandToChat = useSelector((state: DefaultRootState) => state.modals.helpModal.shouldExpandToChat);

  // Handle Redux state changes for native (web uses HelpSidePanel, native uses HelpModal.native)
  useEffect(() => {
    if (chatRestoration && chatRestoration.messages && chatRestoration.messages.length > 0) {
      console.log('💬 [HelpSidePanel] Chat restoration from Redux (web):', chatRestoration);
      setChatMessages(chatRestoration.messages);
      setChatStarted(true);

      // Show success message
      setTimeout(() => {
        console.log(`💬 [HelpSidePanel] Chat "${chatRestoration.chatTitle}" restored successfully`);
      }, 100);

      // Clear the restoration data after processing
      dispatch({ type: 'RESTORE_CHAT_HISTORY', payload: null });
    }
  }, [chatRestoration, setChatMessages, setChatStarted, dispatch]);

  useEffect(() => {
    if (shouldExpandToChat) {
      console.log('💬 [HelpSidePanel] Flip to chat from Redux (web)');
      setFlip(true);
      setChatStarted(true);

      // Reset the expand flag
      dispatch({ type: 'FLIP_HELP_TO_CHAT' });
    }
  }, [shouldExpandToChat, setFlip, setChatStarted, dispatch]);

  // Handle window events for web
  useEffect(() => {
    if (Platform.OS !== 'web') return; // Only for web

    const handleRestoreChat = (event: CustomEvent) => {
      console.log('💬 Chat restoration request received (web):', event.detail);
      const { messages, chatTitle, chatId } = event.detail;

      if (messages && messages.length > 0) {
        console.log('💬 Restoring chat with', messages.length, 'messages');
        setChatMessages(messages);
        setChatStarted(true);

        // Show success message
        setTimeout(() => {
          console.log(`💬 Chat "${chatTitle}" restored successfully`);
        }, 100);
      }
    };

    const handleFlipToChat = (event: CustomEvent) => {
      console.log('💬 Flip to chat request received (web):', event.detail);
      if (event.detail?.shouldFlip) {
        setFlip(true);
        setChatStarted(true);
        console.log('💬 Flipped to chat view');
      }
    };

    window.addEventListener('restoreChatHistory', handleRestoreChat as EventListener);
    window.addEventListener('flipToChat', handleFlipToChat as EventListener);

    return () => {
      window.removeEventListener('restoreChatHistory', handleRestoreChat as EventListener);
      window.removeEventListener('flipToChat', handleFlipToChat as EventListener);
    };
  }, [setChatMessages, setChatStarted, setFlip]);

  // Access utilities context for intelligent assistance
  const { utilities, searchUtilities, getUtilitiesMetadata, getUtilityCodeSnippet } = useUtilities();

  // Performance optimized editor context - memoized to prevent unnecessary re-renders
  const editorContextHook = useEditorContext();
  const {
    editorContext,
    isEditorConnected,
    insertContent,
    setContent,
    getContent,
  } = useMemo(() => ({
    editorContext: editorContextHook.editorContext,
    isEditorConnected: editorContextHook.isEditorConnected,
    insertContent: editorContextHook.insertContent,
    setContent: editorContextHook.setContent,
    getContent: editorContextHook.getContent,
  }), [editorContextHook.editorContext]); // Only re-memoize when editor context changes

  // Memoized editor content with performance optimization and debouncing
  const memoizedEditorContent = useMemo(() => {
    if (!isEditorConnected) return null;
    
    // Throttle content retrieval to prevent excessive calls
    const getContentThrottled = throttle(() => {
      try {
        const content = getContent();
        return content && content.length > 0 ? content : null;
      } catch (error) {
        console.error('Error getting editor content:', error);
        return null;
      }
    }, 300); // Throttle to 300ms
    
    return getContentThrottled();
  }, [isEditorConnected, getContent]);

  // Memoize border gradients to prevent recreation on every render
  const borderGradients = useMemo(() => [
    ['#5ac0ea', '#00ffcc', '#ff00cc', '#ffaa00'],
    ['#ff00cc', '#ffaa00', '#5ac0ea', '#00ffcc'],
    ['#ffaa00', '#5ac0ea', '#00ffcc', '#ff00cc'],
    ['#00ffcc', '#ff00cc', '#ffaa00', '#5ac0ea'],
  ] as const, []);

  const [borderGradientIndex, setBorderGradientIndex] = useState(0);

  // Only run gradient animation when panel is visible and reduce update frequency
  useEffect(() => {
    if (!visibleState) return;

    const interval = setInterval(() => {
      setBorderGradientIndex(prevIndex => (prevIndex + 1) % borderGradients.length);
    }, 5000); // Significantly reduced frequency for better performance
    return () => clearInterval(interval);
  }, [visibleState, borderGradients.length]);

  // Memoize gradient colors to prevent recalculation
  const borderGradientColors = useMemo(() =>
    borderGradients[borderGradientIndex],
    [borderGradients, borderGradientIndex]
  );

  // Memoize disclaimer function to prevent recreation
  const addDisclaimer = useCallback(() => {
    return new Promise<void>((resolve) => {
      setChatMessages(previous => GiftedChat.append(previous, [{
        _id: Math.random().toString(36).substring(7),
        text: "AI generated responses are not always accurate, please use caution when using this feature.",
        isDisclaimer: true,
        system: true,
        createdAt: new Date(),
        user: { _id: "AI" }
      }]))
      resolve();
    })
  }, []);

  // Optimized AI query handler with performance improvements
  const _handleQuery = useCallback(async (query) => {
    console.log('HelpSidePanel - Starting AI query:', query);

    // Store the current user query for data extraction
    setCurrentUserQuery(query);

    // Prevent rapid successive queries or queries when panel is not visible
    if (inputDisabled || !visibleState) return;

    if (!chatMessages.length) {
      addDisclaimer();
    }
    if (!AIHelp) {
      setAIHelp(true);
    }
    if (!chatStarted) {
      setChatStarted(true);
    }

    // Add user message with optimized user object
    const userMessage = {
      _id: Math.random().toString(36).substring(7),
      text: query,
      createdAt: new Date(),
      user: { _id: globalUser, avatar: userAvatar?.img }
    };
    setChatMessages(previousMessages => GiftedChat.append(previousMessages, [userMessage]));

    disableInput(true);

    try {
      // Performance optimization: Get editor content using memoized version
      let editorContent = null;
      let hasEditorContent = false;
      
      if (isEditorConnected && memoizedEditorContent) {
        editorContent = memoizedEditorContent.substring(0, 1000); // Limit content for performance
        hasEditorContent = editorContent && editorContent.trim().length > 0;
      }

      // Enhanced query with optimized context and utilities awareness
      let enhancedQuery = query;
      
      // Add utilities context if relevant
      const utilitiesMetadata = getUtilitiesMetadata();
      const relevantUtilities = searchUtilities(query);
      
      if (isEditorConnected) {
        if (hasEditorContent) {
          enhancedQuery += `\n\nContext: I'm working on content that currently contains: "${editorContent}${memoizedEditorContent && memoizedEditorContent.length > 1000 ? '...' : ''}"`;
        } else {
          enhancedQuery += "\n\nNote: I'm working in a content editor and would like the response formatted for easy insertion.";
        }
      }

      // Add utilities context if query seems development-related
      const devKeywords = ['code', 'function', 'utility', 'generate', 'validate', 'encrypt', 'hash', 'password', 'jwt', 'token', 'cache', 'compress'];
      const isDevQuery = devKeywords.some(keyword => query.toLowerCase().includes(keyword));
      
      if (isDevQuery || relevantUtilities.length > 0) {
        enhancedQuery += `\n\nAvailable Utilities Context:\nI have access to ${utilitiesMetadata.totalCount} utility functions across categories: ${Object.keys(utilitiesMetadata.categoryCounts).join(', ')}. `;
        
        if (relevantUtilities.length > 0) {
          enhancedQuery += `Relevant utilities for this query: ${relevantUtilities.map(u => `${u.title} (${u.category})`).join(', ')}. `;
          enhancedQuery += `Feel free to suggest using these utilities or provide code examples. If you recommend a utility, mention that it's available in the Utilities section.`;
        } else {
          enhancedQuery += `If this query relates to development utilities, I have functions for: Password Generation, JWT Handling, Data Validation, File Compression, and Cache Management.`;
        }
      }

      // Performance optimization: Limit chat history to recent messages
      console.log('🔍 HelpSidePanel - Current chatMessages for history extraction:', chatMessages);
      const recentMessages = chatMessages.slice(-10); // Only use last 10 messages for performance
      const existingHistory = convertGiftedChatToHistory(recentMessages);
      console.log('🔍 HelpSidePanel - Extracted existing history:', existingHistory);
      const sanitizedHistory = sanitizeHistoryForAPI(existingHistory);

      const fullHistory = [
        ...sanitizedHistory,
        { role: 'user' as const, content: query.trim(), timestamp: new Date() }
      ];
      console.log('🔍 HelpSidePanel - Full history to send (limited for performance):', JSON.stringify(fullHistory, null, 2));

      // Performance monitoring
      const startTime = Date.now();
      const data = await handleHelp(isEditorConnected, enhancedQuery, fullHistory);
      const apiCallTime = Date.now() - startTime;
      console.log(`AI API call completed in ${apiCallTime}ms`);
      
      console.log('HelpSidePanel - Raw AI Response:', JSON.stringify(data, null, 2));
      console.log('HelpSidePanel - AI Response Type:', typeof data);
      console.log('HelpSidePanel - AI Response.response:', data.response);
      console.log('HelpSidePanel - AI Response.response Type:', typeof data.response);

      // Check if data is actually an array or object
      if (Array.isArray(data)) {
        console.log('Data is an array with length:', data.length);
        console.log('First array item:', JSON.stringify(data[0], null, 2));
      } else if (typeof data === 'object' && data !== null) {
        console.log('Data is object with keys:', Object.keys(data));
      }

      // Process response with error handling
      let responseText;

      if (Array.isArray(data)) {
        console.log('Data is an array with length:', data.length);
        console.log('First array item:', JSON.stringify(data[0], null, 2));

        // If it's an array, join all responses
        const allResponses = data.map((item: any) => {
          if (typeof item === 'string') return item;
          if (item && typeof item.response === 'string') return item.response;
          if (item && typeof item.text === 'string') return item.text;
          if (item && typeof item.content === 'string') return item.content;
          // If item is an object but not a recognized format, convert to string
          return typeof item === 'object' ? JSON.stringify(item) : String(item);
        }).filter(Boolean).join('\n\n');

        responseText = allResponses || 'No valid responses found in array';

      } else if (typeof data === 'object' && data !== null) {
        console.log('Data is object with keys:', Object.keys(data));

        // Try different possible response properties with type safety
        const dataObj = data as any;
        if (typeof dataObj.response === 'string' && dataObj.response.trim().length > 0) {
          responseText = dataObj.response;
        } else if (typeof dataObj.text === 'string' && dataObj.text.trim().length > 0) {
          responseText = dataObj.text;
        } else if (typeof dataObj.content === 'string' && dataObj.content.trim().length > 0) {
          responseText = dataObj.content;
        } else if (typeof dataObj.message === 'string' && dataObj.message.trim().length > 0) {
          responseText = dataObj.message;
        } else if (dataObj.response === null || dataObj.response === undefined) {
          // Handle null/undefined response specifically
          responseText = 'Sorry, I was unable to generate a response to your question. Please try rephrasing or asking something else.';
        } else {
          // If none of the expected properties are strings, stringify the whole object
          responseText = JSON.stringify(data, null, 2);
        }
      } else {
        responseText = String(data || 'Unknown response format');
      }

      console.log('HelpSidePanel - Final response text:', responseText);

      // Analyze USER QUERY (not AI response) for actionable intents
      // This prevents false positives from AI's explanatory text
      const userQuery = query || '';

      // Use local state check instead of just isMapperReady() since fallback mode is ready too
      if (isMapperInitialized && userQuery.trim().length > 0) {
        analyzeResponse(userQuery).then(matches => {
          if (matches.length > 0) {
            setPendingActions(prev => {
              // Avoid duplicates
              const existingIntents = new Set(prev.map(a => a.intent));
              const newActions = matches.filter(a => !existingIntents.has(a.intent));
              return [...prev, ...newActions];
            });
          }
        }).catch(error => {
          console.error('❌ Failed to analyze AI response:', error);
        });
      }

      // Add AI response with optimized editor actions (only if connected)
      const aiMessage = {
        _id: Math.random().toString(36).substring(7),
        text: responseText,
        createdAt: new Date(),
        system: true,
        received: true,
        // Add context-aware quick actions
        ...(isEditorConnected && {
          quickReplies: {
            type: 'radio',
            keepIt: true,
            values: [
              {
                title: '📝 Insert into Editor',
                value: 'insert_editor',
                messageId: Math.random().toString(36).substring(7),
              },
              {
                title: '🔄 Replace All Content',
                value: 'replace_editor',
                messageId: Math.random().toString(36).substring(7),
              },
              {
                title: '📋 Copy to Clipboard',
                value: 'copy_clipboard',
                messageId: Math.random().toString(36).substring(7),
              },
              ...(relevantUtilities.length > 0 ? [{
                title: '🛠️ View Related Utilities',
                value: 'show_utilities',
                messageId: Math.random().toString(36).substring(7),
              }] : [])
            ]
          }
        })
      };
      setChatMessages(prevMessages => GiftedChat.append(prevMessages, [aiMessage]));

    } catch (err) {
      console.error('AI Query Error:', err, 'Model was ', process.env.EXPO_PUBLIC_AI_MODEL, AI_MODEL);
      setChatMessages(previousMessages =>
        GiftedChat.append(previousMessages, [
          {
            _id: Math.random().toString(36).substring(7),
            text: 'Sorry, I am not able to answer that right now. Please try again later.',
            createdAt: new Date(),
            system: true,
            received: true
          }
        ])
      );
    } finally {
      disableInput(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [
    chatMessages, 
    AIHelp, 
    chatStarted, 
    globalUser, 
    userAvatar?.img, 
    isEditorConnected, 
    addDisclaimer, 
    memoizedEditorContent, // Use memoized content instead of getContent
    inputDisabled, 
    visibleState
  ]);

  // Handle quick reply actions - optimized with stable message lookup
  const handleQuickReply = useCallback((reply) => {
    if (!reply || !reply.value) return;

    // Find the message that triggered this quick reply
    const triggerMessage = chatMessages.find(msg =>
      msg.quickReplies?.values?.some(val => val.messageId === reply.messageId)
    );

    if (!triggerMessage) return;

    const content = triggerMessage.text;

    switch (reply.value) {
      case 'insert_editor':
        if (insertContent(content)) {
          // Add confirmation message
          setChatMessages(prevMessages => GiftedChat.append(prevMessages, [{
            _id: Math.random().toString(36).substring(7),
            text: '✅ Content successfully inserted into editor! You can continue editing or ask for more content.',
            createdAt: new Date(),
            system: true,
            received: true
          }]));
        }
        break;

      case 'replace_editor':
        if (setContent(content)) {
          // Add confirmation message
          setChatMessages(prevMessages => GiftedChat.append(prevMessages, [{
            _id: Math.random().toString(36).substring(7),
            text: '✅ Editor content completely replaced! The new content is now ready for editing.',
            createdAt: new Date(),
            system: true,
            received: true
          }]));
        }
        break;

      case 'copy_clipboard':
        if (typeof navigator !== 'undefined' && navigator.clipboard) {
          navigator.clipboard.writeText(content).then(() => {
            // Add confirmation message
            setChatMessages(prevMessages => GiftedChat.append(prevMessages, [{
              _id: Math.random().toString(36).substring(7),
              text: '✅ Content copied to clipboard! You can now paste it wherever you need it.',
              createdAt: new Date(),
              system: true,
              received: true
            }]));
          });
        }
        break;

      case 'show_utilities':
        // Generate utilities overview message
        const utilitiesOverview = utilities.map(utility => 
          `**${utility.title}** (${utility.category})\n${utility.description}`
        ).join('\n\n');
        
        setChatMessages(prevMessages => GiftedChat.append(prevMessages, [{
          _id: Math.random().toString(36).substring(7),
          text: `🛠️ **Available Development Utilities:**\n\n${utilitiesOverview}\n\n💡 *Visit the Utilities screen to explore full code examples and copy implementations.*`,
          createdAt: new Date(),
          system: true,
          received: true
        }]));
        break;
    }
  }, [chatMessages, insertContent, setContent]);

  // Function to format the entire conversation for sending to editor
  const formatConversationForEditor = useCallback(() => {
    if (!chatMessages.length) return '';

    const conversationTitle = `# AI Conversation Summary\n\n*Generated on ${moment().format("MMMM Do YYYY, h:mm a")}*\n\n---\n\n`;

    // Filter out disclaimer and confirmation messages
    const meaningfulMessages = chatMessages.filter(msg =>
      !msg.isDisclaimer &&
      !msg.text.includes('✅') &&
      !msg.text.includes('❌')
    );

    if (!meaningfulMessages.length) return conversationTitle + 'No meaningful conversation content to export.';

    const formattedMessages = meaningfulMessages.map((message, index) => {
      const isUser = message.user && message.user._id !== "AI" && !message.system;
      const timestamp = moment(message.createdAt).format("h:mm a");

      if (isUser) {
        return `## 🙋 User Question (${timestamp})\n${message.text}\n`;
      } else if (message.system) {
        return `## 🤖 AI Response (${timestamp})\n${message.text}\n`;
      }
      return '';
    }).filter(Boolean);

    return conversationTitle + formattedMessages.join('\n---\n\n');
  }, [chatMessages]);

  // Function to generate a concise summary of the conversation
  const generateConversationSummary = useCallback(() => {
    if (!chatMessages.length) return '';

    const meaningfulMessages = chatMessages.filter(msg =>
      !msg.isDisclaimer &&
      !msg.text.includes('✅') &&
      !msg.text.includes('❌')
    );

    if (!meaningfulMessages.length) return '';

    const userQuestions = meaningfulMessages.filter(msg =>
      msg.user && msg.user._id !== "AI" && !msg.system
    );

    const aiResponses = meaningfulMessages.filter(msg => msg.system);

    const summaryTitle = `# Conversation Summary\n\n*Generated on ${moment().format("MMMM Do YYYY, h:mm a")}*\n\n`;

    let summary = summaryTitle;

    if (userQuestions.length > 0) {
      summary += `## Key Topics Discussed:\n`;
      userQuestions.forEach((msg, index) => {
        summary += `${index + 1}. ${msg.text.split('\n')[0].substring(0, 100)}${msg.text.length > 100 ? '...' : ''}\n`;
      });
      summary += '\n';
    }

    if (aiResponses.length > 0) {
      summary += `## AI Insights & Recommendations:\n`;
      // Get the last AI response as the main insight
      const lastResponse = aiResponses[aiResponses.length - 1];
      const responsePreview = lastResponse.text.substring(0, 300) + (lastResponse.text.length > 300 ? '...' : '');
      summary += responsePreview + '\n\n';
    }

    summary += `**Total Questions:** ${userQuestions.length}  \n**Total AI Responses:** ${aiResponses.length}  \n**Conversation Duration:** ${meaningfulMessages.length > 1 ? moment(meaningfulMessages[meaningfulMessages.length - 1].createdAt).diff(meaningfulMessages[0].createdAt, 'minutes') + ' minutes' : 'Less than a minute'}`;

    return summary;
  }, [chatMessages]);

  // Memoize quick action buttons to prevent re-renders and add throttling
  const quickActionButtons = useMemo(() => [
    { text: "✍️ Intro", query: "Write a professional introduction paragraph" },
    { text: "📝 List", query: "Create a bulleted list of key points" },
    { text: "📋 Summary", query: "Write a professional summary paragraph" },
    { text: "🔢 Steps", query: "Create step-by-step instructions" },
    { text: "🛠️ Utilities", query: "Show me available development utilities and their use cases" },
    { text: "🔐 Security", query: "Generate a secure password using best practices" }
  ], []);

  // Throttled query handler to prevent rapid API calls
  const throttledHandleQuery = useMemo(
    () => throttle(_handleQuery, 1000, { leading: true, trailing: false }),
    [_handleQuery]
  );

  const handleClose = useCallback(() => {
    dispatch({ type: HIDE_HELP_MODAL, payload: null });
  }, [dispatch]);

  const handleToggleExpand = useCallback(() => {
    dispatch({ type: HIDE_HELP_MODAL });
    dispatch({ type: TOGGLE_EASY_MODE });
  }, [dispatch]);


  // Animate width changes smoothly
  useEffect(() => {
    Animated.timing(animatedWidth, {
      toValue: isExpanded ? width / 2 : 400,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [isExpanded, animatedWidth]);

  // Add smooth transition when entering chat mode
  const [chatTransitionComplete, setChatTransitionComplete] = useState(false);
  useEffect(() => {
    if (chatStarted) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setChatTransitionComplete(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setChatTransitionComplete(false);
    }
  }, [chatStarted]);

  const handleFlip = useCallback((selection) => {
    setFlip(!flip);
    if (selection) {
      setSelection(selection)
    }
  }, [flip]);

  // useEffect(() => {
  //   if (typeof window !== "undefined") {
  //     // Add CSS for smooth gradient border transitions only
  //     const style = document.createElement('style');
  //     style.id = 'help-panel-gradient-styles';
  //     style.textContent = `
  //       .gradient-border-container {
  //         position: relative;
  //       }

  //       .gradient-border-container::before {
  //         content: '';
  //         position: absolute;
  //         top: -2px;
  //         left: -2px;
  //         right: -2px;
  //         bottom: -2px;
  //         border-radius: 22px;
  //         padding: 2px;
  //         background: linear-gradient(45deg, var(--gradient-colors));
  //         mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  //         mask-composite: xor;
  //         -webkit-mask-composite: xor;
  //         transition: background 1.5s cubic-bezier(0.4, 0, 0.2, 1);
  //         z-index: -1;
  //       }

  //       .gradient-border-container.animate::before {
  //         animation: borderGradientShift 2s ease-in-out infinite;
  //       }

  //       @keyframes borderGradientShift {
  //         0%, 100% { 
  //           filter: brightness(1) saturate(1) hue-rotate(0deg);
  //         }
  //         25% { 
  //           filter: brightness(1.1) saturate(1.2) hue-rotate(15deg);
  //         }
  //         50% { 
  //           filter: brightness(1.2) saturate(1.4) hue-rotate(30deg);
  //         }
  //         75% { 
  //           filter: brightness(1.1) saturate(1.2) hue-rotate(15deg);
  //         }
  //       }
  //     `;

  //     // Remove existing style if present
  //     const existingStyle = document.getElementById('help-panel-gradient-styles');
  //     if (existingStyle) {
  //       document.head.removeChild(existingStyle);
  //     }

  //     document.head.appendChild(style);

  //     return () => {
  //       const styleElement = document.getElementById('help-panel-gradient-styles');
  //       if (styleElement) {
  //         document.head.removeChild(styleElement);
  //       }
  //     };
  //   }
  // }, []);

  return visibleState && (
    <Animatable.View
      animation="slideInRight"
      duration={500}
      style={{ overflow: 'visible', maxHeight: '96%' }}
    >
      <Animated.View
        style={{
          width: animatedWidth,
          height: '100%',
          backgroundColor: theme
            ? 'rgba(248, 250, 252, 0.95)'
            : 'rgba(15, 23, 42, 0.95)',
          backdropFilter: 'blur(20px)',
          position: isExpanded ? 'fixed' : 'relative',
          right: isExpanded ? 70 : undefined,
          top: isExpanded ? 0 : undefined,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          overflow: 'hidden',
          flexDirection: 'column',
          borderLeftWidth: 1,
          borderColor: theme
            ? 'rgba(0, 0, 0, 0.08)'
            : 'rgba(255, 255, 255, 0.12)',
          shadowColor: theme ? '#000' : '#000',
          shadowOffset: { width: -2, height: 0 },
          shadowOpacity: theme ? 0.1 : 0.4,
          shadowRadius: 20,
          elevation: 12,
          zIndex: isExpanded ? 9999 : 1,
        }}
      >
        {/* Expand/Collapse Button */}
        <Animatable.View
          animation="bounceIn"
          delay={150}
          style={{
            position: 'absolute',
            right: 60,
            top: 12,
            backgroundColor: theme
              ? 'rgba(90, 192, 234, 0.9)'
              : 'rgba(90, 192, 234, 0.8)',
            borderRadius: 20,
            width: 36,
            height: 36,
            zIndex: 1000,
            shadowColor: '#5ac0ea',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.4,
            shadowRadius: 8,
            elevation: 6,
            backdropFilter: 'blur(10px)',
            borderWidth: 1,
            borderColor: theme
              ? 'rgba(255, 255, 255, 0.3)'
              : 'rgba(255, 255, 255, 0.1)',
          }}
        >
          <Pressable
            style={{
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center'
            }}
            onPress={handleToggleExpand}
          >
            <MaterialIcons
              name={isExpanded ? "fullscreen-exit" : "fullscreen"}
              size={20}
              color="white"
            />
          </Pressable>
        </Animatable.View>

      {/* Modern Close Button */}
      <Animatable.View
        animation="bounceIn"
        delay={200}
        style={{
          position: 'absolute',
          right: 12,
          top: 12,
          backgroundColor: theme
            ? 'rgba(239, 68, 68, 0.9)'
            : 'rgba(239, 68, 68, 0.8)',
          borderRadius: 20,
          width: 36,
          height: 36,
          zIndex: 1000,
          shadowColor: '#ef4444',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.4,
          shadowRadius: 8,
          elevation: 6,
          backdropFilter: 'blur(10px)',
          borderWidth: 1,
          borderColor: theme
            ? 'rgba(255, 255, 255, 0.3)'
            : 'rgba(255, 255, 255, 0.1)',
        }}
      >
        <Pressable
          style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center'
          }}
          onPress={handleClose}
        >
          <MaterialIcons name="close" size={20} color="white" />
        </Pressable>
      </Animatable.View>

      {/* Modern Header with Enhanced Gradient */}
      <LinearGradient
        colors={theme
          ? ['rgba(90, 192, 234, 0.9)', 'rgba(90, 192, 234, 0.7)', 'rgba(90, 192, 234, 0.3)']
          : ['rgba(30, 41, 59, 0.95)', 'rgba(51, 65, 85, 0.8)', 'rgba(71, 85, 105, 0.4)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{
          height: 130,
          padding: 20,
          flexDirection: 'row',
          gap: 12,
          overflow: 'hidden',
          borderBottomWidth: 1,
          borderBottomColor: theme
            ? 'rgba(0, 0, 0, 0.1)'
            : 'rgba(255, 255, 255, 0.1)',
        }}
      >
        <Animatable.View
          animation="zoomIn"
          delay={300}
          style={{
            backgroundColor: theme
              ? 'rgba(255, 255, 255, 0.2)'
              : 'rgba(0, 0, 0, 0.3)',
            borderRadius: 50,
            padding: 8,
            alignSelf: 'center',
            backdropFilter: 'blur(10px)',
          }}
        >
          <SSLogo height={70} width={70} viewBox={'0 0 1500 1500'} />
        </Animatable.View>

        <View style={{ gap: 0, flex: 1, justifyContent: 'center' }}>
          <Animatable.View animation="fadeInUp" delay={400}>
            <Title style={{
              color: 'white',
              fontSize: 18,
              fontWeight: '700',
              textShadowColor: 'rgba(0, 0, 0, 0.3)',
              textShadowOffset: { width: 0, height: 1 },
              textShadowRadius: 2,
            }}>
              SmartSaaS Leaf
            </Title>
            <Text style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: 12,
            }}>
              v{Constants.expoConfig?.version}
            </Text>
            <Text style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: 12,
            }}>
              Help for {currentScreen}
            </Text>

            {isEditorConnected && (
              <View style={{ 
                marginTop: 8,
                backgroundColor: 'rgba(16, 185, 129, 0.2)',
                borderRadius: 12,
                paddingHorizontal: 8,
                paddingVertical: 4,
                alignSelf: 'flex-start',
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#10b981',
                    marginRight: 6
                  }} />
                  <Text style={{
                    color: '#10b981',
                    fontSize: 11,
                    fontWeight: '600'
                  }}>
                    Editor Connected
                  </Text>
                </View>
              </View>
            )}
          </Animatable.View>
        </View>
      </LinearGradient>

        {/* Content Area with Modern Styling - ChatGPT-like Layout */}
        <View style={{
          flex: 1,
          position: 'relative',
          paddingBottom: (!chatStarted && isEditorConnected) ? 130 : 0, // Add padding for floating buttons when not in chat mode
        }}>
        {chatStarted ? (
            // Full-height ChatGPT-like chat interface
            <View style={{
              flex: 1,
              backgroundColor: theme ? '#ffffff' : '#1a1a1a',
              position: 'relative',
              minHeight: 0, // Important for flex children to shrink properly
            }}>
              {/* Action Chips for Chat Mode */}
              {pendingActions.length > 0 && (
                <>
                  <ActionChips
                    actions={pendingActions}
                    onExecute={handleExecuteAction}
                    onDismiss={handleDismissAction}
                    onDismissAll={handleDismissAllActions}
                    onFeedback={handleInlineFeedback}
                  />
                </>
              )}

              <ChatInterface
                chatMessages={chatMessages}
                setChatMessages={setChatMessages}
                setChatStarted={setChatStarted}
                setFlip={setFlip}
                flip={flip}
                handleQuickReply={handleQuickReply}
                disableInput={disableInput}
                isEditorConnected={isEditorConnected}
                insertContent={insertContent}
                setContent={setContent}
                formatConversationForEditor={formatConversationForEditor}
                generateConversationSummary={generateConversationSummary}
                chatOnly={true}
                onChatSaved={handleChatSaved}
              />
            </View>
        ) : (
          <FlipCard
            clickable={false}
            flipVertical={false}
            flip={flip}
            flipHorizontal={true}
            style={{ flex: 1 }}
          >
              {/* Help Topics Side */}
              <View style={[
                styles.face,
                {
                  flex: 1,
                  alignSelf: 'stretch',
                  minHeight: '100%',
                  backgroundColor: theme
                    ? 'rgba(255, 255, 255, 0.8)'
                    : 'rgba(30, 41, 59, 0.8)',
                  backdropFilter: 'blur(10px)',
                }
              ]}>
                <View style={{
                  flex: 1,
                  height: '100%',
                  justifyContent: 'center',
                  alignSelf: 'stretch',
                  alignContent: 'center',
                  alignItems: 'center',
                  padding: 16,
                  maxWidth: '100%',
                }}>
                  <Suspense fallback={
                    <Animatable.View
                      animation="pulse"
                      iterationCount="infinite"
                      style={{
                        backgroundColor: theme
                          ? 'rgba(90, 192, 234, 0.1)'
                          : 'rgba(90, 192, 234, 0.2)',
                        borderRadius: 50,
                        padding: 20,
                        marginBottom: 16,
                      }}
                    >
                      <MaterialIcons
                        name="help-outline"
                        size={32}
                        color="rgb(90,192,234)"
                      />
                    </Animatable.View>
                  }>
                    <ShowHelp searchTerm={helpSearch || currentScreen} handleFlip={handleFlip} />
                  </Suspense>
                </View>
              </View>

            {/* Chat Side */}
            <View style={[
              styles.back,
              {
                flex: 1,
                padding: 12,
                alignSelf: 'stretch',
                minHeight: '100%',
                backgroundColor: theme
                  ? 'rgba(255, 255, 255, 0.9)'
                  : 'rgba(30, 41, 59, 0.9)',
                backdropFilter: 'blur(15px)',
              }
            ]}>
              <Suspense fallback={
                <Animatable.View
                  animation="pulse"
                  iterationCount="infinite"
                  style={{
                    backgroundColor: theme
                      ? 'rgba(99, 102, 241, 0.1)'
                      : 'rgba(99, 102, 241, 0.2)',
                    borderRadius: 50,
                    padding: 20,
                    marginBottom: 16,
                    alignSelf: 'center',
                  }}
                >
                  <MaterialIcons
                    name="chat"
                    size={32}
                    color="rgb(99, 102, 241)"
                  />
                </Animatable.View>
              }>
                <HelpChat selection={selection} flip={() => setFlip(!flip)} />
              </Suspense>
            </View>
          </FlipCard>
        )}
      </View>

        {/* ChatGPT-like Floating Input for Chat Mode */}
        {chatStarted ? (
          <View style={{
            position: 'absolute',
            bottom: 20,
            left: 20,
            right: 20,
            zIndex: 1000,
            pointerEvents: 'box-none', // Allow touches to pass through to scrollable content
          }}>
            {/* Quick Action Buttons - Floating above input */}
            {isEditorConnected && (
              <Animatable.View
                animation={chatTransitionComplete ? "fadeInUp" : undefined}
                duration={500}
                style={{
                  marginBottom: 12,
                  opacity: chatTransitionComplete ? 1 : 0,
                  transform: [{ translateY: chatTransitionComplete ? 0 : 10 }],
                }}
              >
                <XStack gap={8} justifyContent="center" flexWrap="wrap">
                  {quickActionButtons.slice(0, 4).map((button, index) => (
                    <Animatable.View
                      key={index}
                      animation="bounceIn"
                      delay={100 * index}
                    >
                      <Pressable
                        style={({ pressed }) => ({
                          backgroundColor: theme
                            ? pressed ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.8)'
                            : pressed ? 'rgba(30, 41, 59, 0.9)' : 'rgba(30, 41, 59, 0.8)',
                          paddingHorizontal: 12,
                          paddingVertical: 6,
                          borderRadius: 20,
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 4,
                          transform: pressed ? [{ scale: 0.95 }] : [{ scale: 1 }],
                          borderWidth: 1,
                          borderColor: theme
                            ? 'rgba(0, 0, 0, 0.1)'
                            : 'rgba(255, 255, 255, 0.2)',
                          shadowColor: theme ? '#000' : '#fff',
                          shadowOffset: { width: 0, height: 2 },
                          shadowOpacity: theme ? 0.1 : 0.3,
                          shadowRadius: 8,
                          elevation: 5,
                        })}
                        onPress={() => throttledHandleQuery(button.query)}
                      >
                        <Text style={{
                          fontSize: 11,
                          color: theme ? '#374151' : '#e5e7eb',
                          fontWeight: '600',
                        }}>
                          {button.text}
                        </Text>
                      </Pressable>
                    </Animatable.View>
                  ))}
                </XStack>
              </Animatable.View>
            )}

            {/* Floating Input Container */}
            <Animatable.View
              animation={chatTransitionComplete ? "slideInUp" : undefined}
              duration={400}
              style={{
                backgroundColor: theme ? '#ffffff' : '#374151',
                borderRadius: 24,
                shadowColor: theme ? '#000' : '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: theme ? 0.15 : 0.4,
                shadowRadius: 12,
                elevation: 8,
                borderWidth: 1,
                borderColor: theme
                  ? 'rgba(0, 0, 0, 0.08)'
                  : 'rgba(255, 255, 255, 0.1)',
                overflow: 'hidden',
                opacity: chatTransitionComplete ? 1 : 0,
                transform: [{ translateY: chatTransitionComplete ? 0 : 20 }],
                pointerEvents: 'auto', // Ensure input container can receive touches
              }}
            >
              <MyInputToolbar
                disableEntry={inputDisabled}
                messageInput
                removeTools
                customSendIcon={(
                  <View style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: theme
                      ? 'rgba(90, 192, 234, 0.1)'
                      : 'rgba(90, 192, 234, 0.2)',
                    borderRadius: 20,
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                    marginRight: 8,
                  }}>
                    {isEditorConnected && (
                      <Text style={{
                        fontSize: 9,
                        color: '#10b981',
                        marginRight: 6,
                        fontWeight: '700',
                      }}>
                        READY
                      </Text>
                    )}
                    <AnimatedLottieView
                      source={require("@assets/animations/automation_robot.json")}
                      style={{ width: 20, height: 20 }}
                      autoPlay={!inputDisabled}
                      loop={!inputDisabled}
                    />
                  </View>
                )}
                containerStyle={{
                  backgroundColor: 'transparent',
                  borderRadius: 24,
                  paddingHorizontal: 16,
                  paddingVertical: 12,
                  minHeight: 56,
                  borderWidth: 0,
                  shadowOpacity: 0,
                  elevation: 0,
                }}
                textInputStyle={{
                  backgroundColor: 'transparent',
                  borderWidth: 0,
                  fontSize: 16,
                  lineHeight: 22,
                  color: theme ? '#374151' : '#e5e7eb',
                  paddingHorizontal: 0,
                  paddingVertical: 0,
                  minHeight: 24,
                  maxHeight: 120,
                }}
                sendMessageCallback={(query) => throttledHandleQuery(query)}
                placeHolder={isEditorConnected
                  ? "Message AI assistant..."
                  : "Ask a question..."
                }
              />
            </Animatable.View>
          </View>
        ) : (
          /* Traditional Footer for Non-Chat Mode */
          <View style={{
            alignSelf: "stretch",
            alignContent: "flex-end",
              minWidth: "99%",
            backgroundColor: theme
              ? 'rgba(255, 255, 255, 0.95)'
              : 'rgba(30, 41, 59, 0.95)',
              position: 'relative', // For ActionChips positioning
            backdropFilter: 'blur(20px)',
            borderTopWidth: 1,
            borderTopColor: theme
              ? 'rgba(0, 0, 0, 0.1)'
              : 'rgba(255, 255, 255, 0.1)',
            shadowColor: theme ? '#000' : '#fff',
            shadowOffset: { width: 0, height: -2 },
            shadowOpacity: theme ? 0.05 : 0.1,
            shadowRadius: 8,
          }}>
              {/* Action Chips - Detected actions from AI responses */}
              {pendingActions.length > 0 && (
                <>
                  <ActionChips
                    actions={pendingActions}
                    onExecute={handleExecuteAction}
                    onDismiss={handleDismissAction}
                    onDismissAll={handleDismissAllActions}
                    onFeedback={handleInlineFeedback}
                  />
                </>
              )}

              {/* Quick Action Buttons - Positioned absolutely to not affect input area */}
            {isEditorConnected && (
              <Animatable.View
                animation="fadeInUp"
                duration={500}
                style={{
                  position: 'absolute',
                  top: -130, // Move up by 80+ pixels from previous position
                  left: 15,
                  right: 15,
                  zIndex: 1000,
                }}
              >
                <XStack gap={8} justifyContent="center" flexWrap="wrap">
                  {quickActionButtons.map((button, index) => (
                    <Animatable.View
                      key={index}
                      animation="bounceIn"
                      delay={100 * index}
                    >
                      <Pressable
                        style={({ pressed }) => ({
                          backgroundColor: theme
                            ? pressed ? 'rgba(90, 192, 234, 0.2)' : 'rgba(90, 192, 234, 0.1)'
                            : pressed ? 'rgba(90, 192, 234, 0.3)' : 'rgba(90, 192, 234, 0.2)',
                          paddingHorizontal: 12,
                          paddingVertical: 8,
                          borderRadius: 16,
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 4,
                          transform: pressed ? [{ scale: 0.95 }] : [{ scale: 1 }],
                          borderWidth: 1,
                          borderColor: theme
                            ? 'rgba(90, 192, 234, 0.3)'
                            : 'rgba(90, 192, 234, 0.4)',
                          shadowColor: 'rgb(90, 192, 234)',
                          shadowOffset: { width: 0, height: 2 },
                          shadowOpacity: 0.2,
                          shadowRadius: 4,
                          elevation: 3,
                        })}
                        onPress={() => throttledHandleQuery(button.query)}
                      >
                        <Text style={{
                          fontSize: 10,
                          color: theme ? 'rgb(90, 192, 234)' : 'rgb(120, 220, 255)',
                          fontWeight: '600',
                        }}>
                          {button.text}
                        </Text>
                      </Pressable>
                    </Animatable.View>
                  ))}
                </XStack>
              </Animatable.View>
            )}

              {/* Input Toolbar - Consistent height regardless of button presence */}
              <MyInputToolbar
                disableEntry={inputDisabled}
                messageInput
                removeTools
                customSendIcon={(
                  <View style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: theme
                      ? 'rgba(90, 192, 234, 0.1)'
                      : 'rgba(90, 192, 234, 0.2)',
                    borderRadius: 20,
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                  }}>
                    {isEditorConnected && (
                      <Text style={{
                        fontSize: 9,
                        color: '#10b981',
                        marginRight: 6,
                        fontWeight: '700',
                      }}>
                        READY
                      </Text>
                    )}
                    <AnimatedLottieView
                      source={require("@assets/animations/automation_robot.json")}
                      style={{ width: 25, height: 25 }}
                      autoPlay={!inputDisabled}
                      loop={!inputDisabled}
                    />
                  </View>
                )}
                containerStyle={{
                  backgroundColor: theme
                    ? 'rgba(255, 255, 255, 0.9)'
                    : 'rgba(51, 65, 85, 0.9)',
                  borderRadius: 0,
                  borderBottomLeftRadius: 16,
                  shadowColor: theme ? '#000' : '#fff',
                  shadowOffset: { width: 0, height: -2 },
                  shadowOpacity: theme ? 0.1 : 0.2,
                  shadowRadius: 8,
                  elevation: 5,
                  minHeight: 60, // Ensure consistent height
                }}
                sendMessageCallback={(query) => throttledHandleQuery(query)}
                placeHolder={isEditorConnected
                  ? "💡 Ask AI and insert content into editor..."
                  : "🔍 Search for help or ask a question"
                }
              />
            </View>
        )}
      </Animated.View>

      {/* Action Data Form Modal */}
      <ActionDataForm
        visible={actionRequiringData !== null}
        actionTemplate={actionRequiringData}
        onSubmit={handleDataFormSubmit}
        onCancel={handleDataFormCancel}
        initialData={extractedFormData}
      />

      {/* Post Execution Feedback Modal */}
      <PostExecutionFeedback
        visible={showPostExecutionFeedback}
        actionDescription={lastExecutedAction?.template?.description || lastExecutedAction?.template?.intent || 'Action'}
        onFeedback={handlePostExecutionFeedback}
        onDismiss={() => {
          setShowPostExecutionFeedback(false);
          setLastExecutedAction(null);
        }}
        autoDismissAfter={5000}
      />
    </Animatable.View>
  );
});

export default HelpSidePanel;
